// Resolves a rich, human-readable store label ("Miami, FL - #1916", full
// address, warehouse name, ...) from a retailer's loaded store catalog.
// Mirrors pinto/utils/storeDisplayLabel.ts.

const formatStoreId = (storeId: string, isCostco: boolean) => {
  const cleaned = storeId.replace("-PENNY-ITEM", "").replace("-CRAZY-DEALS", "").replace("-COUPON", "");
  return isCostco ? cleaned.replace(/-wh$/, "") : cleaned;
};

const formatHomeDepotStoreId = (storeId: string) => storeId.replace(/^0+/, "") || storeId;

export function resolveStoreDisplayLabel(
  retailer: string,
  storeId: string | undefined,
  loadedStores: any[] | null | undefined
): string | null {
  const raw = (storeId || "").trim();
  if (!raw || raw === "all" || raw === "online") return null;

  const isCostco = retailer === "costco";
  const isHomeDepot = retailer === "home_depot";
  const isTarget = retailer === "target";
  const isLowes = retailer === "lowes";
  const isLocalClearance = retailer === "tractor_supply" || retailer === "northern_tool" || retailer === "samsclub";
  const isDollarGeneral = retailer === "dollar_general";
  const isBestBuy = retailer === "bestbuy";
  const fmtId = formatStoreId(raw, isCostco);

  if (!loadedStores?.length) return `#${fmtId}`;

  try {
    if (isHomeDepot) {
      const s = loadedStores.find(
        (x) => x.store_number && formatHomeDepotStoreId(x.store_number) === formatHomeDepotStoreId(fmtId)
      );
      if (s?.city) return `${s.city} - #${formatHomeDepotStoreId(fmtId)}`;
      if (s?.address_1) return s.address_1;
    } else if (isTarget || isLowes) {
      const s = loadedStores.find((x) => String(x["Store No."]) === fmtId);
      const line = [s?.Street, s?.City, s?.State].filter(Boolean).join(", ");
      if (line) return line;
      if (s?.City) return `${s.City} - #${fmtId}`;
    } else if (isLocalClearance) {
      const s = loadedStores.find((x) => String(x.store_number ?? x.store_id).replace(/^0+/, "") === fmtId.replace(/^0+/, ""));
      if (s?.label) return s.label;
      if (s?.city) return `${s.city} - #${fmtId}`;
      if (s?.street) return s.street;
    } else if (isDollarGeneral) {
      const s = loadedStores.find((x) => x.storeNumber === fmtId);
      if (s?.address) return s.address;
      if (s?.city) return `${s.city} - #${fmtId}`;
    } else if (isBestBuy) {
      const s = loadedStores.find((x) => String(x.store_number) === fmtId);
      if (s?.name && s?.city) return `${s.name} - ${s.city}`;
      if (s?.city) return `${s.city} - #${fmtId}`;
    } else if (isCostco) {
      const num = parseInt(fmtId, 10);
      const s = loadedStores.find((x) => x.warehouse_number === num);
      if (s?.warehouse_name) return s.warehouse_name;
      if (s?.address) return s.address;
    } else {
      const s = loadedStores.find((x) => x.store_id === fmtId);
      if (s?.address) return s.address;
      if (s?.city && s?.state) return `${s.city}, ${s.state} - #${fmtId}`;
    }
  } catch {
    // fall through
  }

  return `#${fmtId}`;
}
